/**
 * Orders page: bracket grouping and the end_reason vocabulary.
 *
 * Pure: no Vue, no DOM.
 */

/**
 * The closed end_reason vocabulary (shared/execution/broker_order_record.py),
 * with what each one means and how the page shows it.
 */
export const END_REASONS = {
  filled: { label: 'filled', badge: 'bg-success', title: 'The order filled in full' },
  oco_sibling_filled: { label: 'sibling filled', badge: 'bg-info text-dark',
                        title: 'Cancelled by the venue because the other exit leg filled first' },
  cutoff_1500: { label: '15:00 cut-off', badge: 'bg-warning text-dark',
                 title: 'A resting entry cancelled at the last arming time: price never came back to the level' },
  tif_expired: { label: 'expired', badge: 'bg-warning text-dark', title: 'Its time in force ran out' },
  remainder_cancelled: { label: 'remainder cancelled', badge: 'bg-secondary',
                         title: 'A partly filled entry whose unfilled remainder was cancelled; its legs kept protecting what filled' },
  parent_unfilled: { label: 'entry unfilled', badge: 'bg-light text-dark border',
                     title: 'A dormant bracket leg that ended with its entry, which never filled' },
  cancelled_by_app: { label: 'cancelled by app', badge: 'bg-secondary',
                      title: 'Cancelled by the system: a sibling entry filled, an app-side close, the kill switch or an orphan-leg reconcile' },
  rejected: { label: 'rejected', badge: 'bg-danger', title: 'The broker refused it' },
};

/** "Entries that never filled": the population the page exists to show. */
export const NEVER_FILLED = { bracket_role: 'entry', end_reason: 'cutoff_1500,tif_expired' };

const LEG_ORDER = { stop_leg: 0, target_leg: 1 };

/**
 * The page's rows in display order: every top-level order (an entry or a
 * plain order) in the order the server sent it, each bracket entry followed
 * by its legs, indented. A leg whose entry is not on the page is shown on
 * its own (`orphan`).
 *
 * @param {Array<object>} orders  the filtered page, newest first
 * @param {Array<object>} legs    extra legs of the page's entries
 * @returns {Array<{order: object, depth: number, orphan?: boolean, key: string}>}
 */
export function groupOrders(orders = [], legs = []) {
  const all = [...orders, ...legs];
  const ids = new Set(all.map((o) => o.order_id).filter(Boolean));
  const children = new Map();
  for (const o of all) {
    if (o.parent_order_id && ids.has(o.parent_order_id)) {
      if (!children.has(o.parent_order_id)) children.set(o.parent_order_id, []);
      children.get(o.parent_order_id).push(o);
    }
  }
  const rows = [];
  const placed = new Set();
  const key = (o, i) => o.order_id || `${o.client_order_id || 'refused'}-${i}`;
  orders.forEach((o, i) => {
    if (placed.has(key(o, i))) return;
    const isChild = o.parent_order_id && ids.has(o.parent_order_id);
    if (isChild) return; // drawn under its entry
    rows.push({ order: o, depth: 0, orphan: Boolean(o.parent_order_id), key: key(o, i) });
    placed.add(key(o, i));
    const kids = (children.get(o.order_id) || [])
      .slice()
      .sort((a, b) => (LEG_ORDER[a.bracket_role] ?? 9) - (LEG_ORDER[b.bracket_role] ?? 9));
    for (const kid of kids) {
      if (placed.has(kid.order_id)) continue;
      rows.push({ order: kid, depth: 1, key: kid.order_id });
      placed.add(kid.order_id);
    }
  });
  // A leg on the page whose entry is on the page but was itself a child of
  // nothing drawn (cannot happen with a well-formed page) is still shown.
  orders.forEach((o, i) => {
    if (!placed.has(key(o, i))) rows.push({ order: o, depth: 0, orphan: true, key: key(o, i) });
  });
  return rows;
}

/** "limit / stop" for the table: whichever prices the order carries. */
export function priceText(o) {
  const f = (v) => (v === null || v === undefined ? null : Number(v).toFixed(2));
  const parts = [];
  if (f(o.limit_price)) parts.push(`L ${f(o.limit_price)}`);
  if (f(o.stop_price)) parts.push(`S ${f(o.stop_price)}`);
  return parts.length ? parts.join(' · ') : '—';
}

/** New York wall time of a UTC ISO timestamp, HH:MM:SS. */
export function etTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour12: false });
}
